/**
 * @module host
 * @version 1.0.0
 * @description Módulo HOST (Shell) de Rfacil Materialidad. Es la aplicación principal que orquesta
 * todos los micro-frontends mediante Module Federation. Gestiona autenticación (SSO),
 * enrutamiento global, sidebar de navegación, control de permisos y carga dinámica
 * de los módulos remotos: Autofactura, Materialidad, Gastos y Proveedores.
 * Modificar este archivo fuerza el rebuild completo del módulo host.
 * @build-trigger 2026-03-20
 */
import { StrictMode } from 'react';
import * as ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@rfacil/shared/services';
import App from './app/app';
import './styles.css';
import { buildInfo, formatBuildStamp } from './build-info';
import { registrarServiceWorker } from './registrarServiceWorker';
import { AplicarActualizacionAlNavegar } from './app/components/AplicarActualizacionAlNavegar';

/**
 * @fileoverview Punto de entrada principal de la aplicación host del sistema Rfacil Materialidad.
 * Este archivo inicializa la aplicación React y configura el enrutador.
 *
 * @description
 * - Crea el contenedor raíz de React en el elemento DOM con id 'root'
 * - Envuelve la aplicación en StrictMode para detectar problemas potenciales
 * - Configura BrowserRouter para el manejo de rutas
 * - Configura QueryClientProvider para el manejo del estado del servidor con TanStack Query
 * - Renderiza el componente App que contiene toda la lógica de la aplicación
 */

// Identidad del build al alcance de soporte y QA sin tocar la UI: se lee
// `window.__ANFORA_BUILD__` desde devtools en cualquier entorno desplegado.
window.__ANFORA_BUILD__ = buildInfo;
console.info(
  `ANFORA ${formatBuildStamp(buildInfo)} — build ${buildInfo.builtAt}`
);

// Service worker: se registra en todo build que no sea el servidor de desarrollo (producción, qa,
// demo, test) y deja pendiente la versión nueva que se publique (se aplica al cambiar de pantalla).
// En el servidor de desarrollo da de baja el que hubiera quedado registrado.
void registrarServiceWorker();

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AplicarActualizacionAlNavegar />
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>
);
